import {useCallback, useRef, useState} from "react";
import axios from "axios";
import {COURSE_API_BASE} from "../../../../../utils/apiConfig";
import useEnergy from "../../../../../hooks/useEnergy";

const ENERGY_PER_MISTAKE = 1;

// consumeEnergy() errTypes that mean "request problem", not "not enough energy"
const TECHNICAL_ERR_TYPES = ["server", "network"];

/**
 * Tracks answers/hints for one lesson attempt, charges energy for mistakes
 * (first-time completions only) and submits the result when the lesson ends.
 */
const useLessonSession = ({
                              userId,
                              courseId,
                              contentId,
                              lessonAttemptId,
                              alreadyCompleted,
                          }) => {
    const {consumeEnergy} = useEnergy(userId);

    // Refs: counters must be current at completion time and never trigger re-renders
    const counters = useRef({correct: 0, mistakes: 0, hints: 0});

    const [outOfEnergy, setOutOfEnergy] = useState(false);
    const [completing, setCompleting] = useState(false);
    const [completeError, setCompleteError] = useState("");
    const [result, setResult] = useState(null);

    /** Call from question blocks with true/false for every answer. */
    const recordAnswer = useCallback(
        async (isCorrect) => {
            if (isCorrect) {
                counters.current.correct += 1;
                return;
            }

            counters.current.mistakes += 1;

            // Replaying a finished lesson is free
            if (alreadyCompleted) return;

            const res = await consumeEnergy(ENERGY_PER_MISTAKE);

            if (!res.success && res.errType && !TECHNICAL_ERR_TYPES.includes(res.errType)) {
                setOutOfEnergy(true);
            }
        },
        [alreadyCompleted, consumeEnergy]
    );

    /** Call whenever the learner uses a hint. */
    const recordHint = useCallback(() => {
        counters.current.hints += 1;
    }, []);

    /** Submits the attempt. Resolves to { success }. */
    const completeLesson = useCallback(async () => {
        // Nothing to save or award for a lesson finished before
        if (alreadyCompleted) return {success: true};

        setCompleting(true);
        setCompleteError("");

        try {
            const {data} = await axios.put(
                `${COURSE_API_BASE}/${userId}/lesson-attempt/${lessonAttemptId}/complete`,
                {
                    course_id: courseId,
                    content_id: contentId,
                    lesson_attempt_id: lessonAttemptId,
                    correct_answers: counters.current.correct,
                    mistake_answers: counters.current.mistakes,
                    hint_count: counters.current.hints,
                    status: "completed",
                },
                {withCredentials: true}
            );

            setResult({
                xp: data.xp ?? 0,
                credit: data.credit ?? 0,
                streak: data.streak,
                streakChanged: Boolean(data.streak_changed),
            });

            return {success: true};
        } catch (err) {
            setCompleteError(
                err.response?.data?.message || "خطا در ثبت نتیجه درس"
            );
            return {success: false};
        } finally {
            setCompleting(false);
        }
    }, [alreadyCompleted, userId, courseId, contentId, lessonAttemptId]);

    return {
        recordAnswer,
        recordHint,
        completeLesson,
        completing,
        completeError,
        outOfEnergy,
        result,
    };
};

export default useLessonSession;